import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import BillingStatusIcon from '../components/BillingStatusIcon';
import apiService from '../services/api';
import { buildBillingStateIndex } from '../utils/billing';
import { buildSentIndex } from '../utils/schoolPay';
import { FUNNEL_STATE, funnelStatusFor } from '../utils/funnel';
import { filterAuditTxns, groupAuditByMonth } from '../utils/audit';
import '../styles/StaffPages.css';

/**
 * Every transaction, and how far it got.
 *
 * Merchant Invoices and School Pay each show one side of the money and hide
 * anything that did not reach them. This shows the whole feed a month at a
 * time, carrying the same two status markers those pages use, so a transaction
 * that stalled — never billed, never paid out — is visible rather than simply
 * absent from both.
 */

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : '—';

/**
 * The funnel state.
 *
 * Deliberately untitled. The vendor's own string ('pending' is true of both a
 * Wildfire READY and a Wildfire PENDING) is worth seeing when a row looks
 * wrong, but it lives on the expanded update log, where it can be read rather
 * than hovered for.
 */
const StatusPill = ({ funnel }) => {
  const state = FUNNEL_STATE[funnel] || FUNNEL_STATE.pending;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '100px',
        fontSize: '11px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: state.color,
        background: `${state.color}14`,
      }}
    >
      {state.label}
    </span>
  );
};

/**
 * The School Pay marker: the bare check every other page draws, not a check in
 * a ring. This used to hand-roll its own circled variant, which made the one
 * glyph that means "done" look like two different things depending on which
 * page you were reading. Taking it from FUNNEL_STATE keeps the colour tied to
 * the same source as the rest of the funnel rather than to a copied literal.
 */
const SentCheck = ({ sent, label }) =>
  sent ? (
    <BillingStatusIcon
      state={{ ...FUNNEL_STATE.distributed, label }}
      size={16}
      showTitle={false}
    />
  ) : (
    <span aria-label="Not sent" />
  );

/**
 * Nothing here yet: either still in the funnel, or never applicable.
 *
 * These two used to be drawn apart — a ring for money owed and not yet paid, an
 * em-dash for a transaction with no school attached at all. Received and Paid
 * out now use the ring for both, so the two columns read as one column of
 * markers rather than a mix of glyphs. The distinction is still legible from
 * the row: a School share of '—' is the one that was never owed.
 */
const Awaiting = ({ label }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    role="img"
    aria-label={label}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="#ccc"
      strokeWidth="2"
      strokeDasharray="3 3"
    />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#666"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      transform: open ? 'rotate(90deg)' : 'none',
      transition: 'transform 0.15s ease',
    }}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Every update we hold for one transaction, in the order they arrived.
 *
 * The point is to show what each delivery actually said, including the ones the
 * folded state deliberately ignores — a PAID that arrived before a later
 * PENDING, or the update where a commission was revised to zero. Reading the row
 * above without this is how a returned order looks like a bug.
 */
const Updates = ({ history }) => {
  if (!history || history.loading) {
    return (
      <div style={{ padding: '14px 20px', color: '#999', fontSize: '12px' }}>
        Loading updates…
      </div>
    );
  }
  if (history.error) {
    return (
      <div style={{ padding: '14px 20px', color: '#b00020', fontSize: '12px' }}>
        Could not load the updates.
      </div>
    );
  }
  if (!history.rows.length) {
    return (
      <div style={{ padding: '14px 20px', color: '#999', fontSize: '12px' }}>
        No updates stored. Hand-entered rows have no vendor behind them.
      </div>
    );
  }

  const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const th = {
    textAlign: 'left',
    padding: '0 24px 6px 0',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#999',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: '14px 20px 18px' }}>
      <div
        style={{
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#666',
          marginBottom: '10px',
        }}
      >
        Updates
        <span style={{ fontWeight: 400, color: '#999' }}>
          {' · '}
          {history.rows.length}
        </span>
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr>
            <th style={th}>Date</th>
            <th style={th}>Time</th>
            <th style={{ ...th, textAlign: 'right' }}>Commission</th>
            <th style={th}>Vendor status</th>
            <th style={{ ...th, paddingRight: 0 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.rows.map((e, i) => {
            // The most recent update is the one the row above reflects; the
            // earlier ones are history, so they recede.
            const latest = i === history.rows.length - 1;
            const when = e.at ? new Date(e.at) : null;
            return (
              <tr
                key={`${e.at}-${i}`}
                style={{ color: latest ? '#1a1a1a' : '#888' }}
              >
                <td style={{ padding: '4px 24px 4px 0', whiteSpace: 'nowrap' }}>
                  {when
                    ? when.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '—'}
                </td>
                <td style={{ padding: '4px 24px 4px 0', whiteSpace: 'nowrap' }}>
                  {when
                    ? when.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : '—'}
                </td>
                <td
                  style={{
                    padding: '4px 24px 4px 0',
                    textAlign: 'right',
                    fontWeight: latest ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {money(e.commission)}
                </td>
                <td
                  style={{
                    padding: '4px 24px 4px 0',
                    fontWeight: latest ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {e.status || '—'}
                </td>
                <td style={{ padding: '4px 0', whiteSpace: 'nowrap' }}>
                  {/* Derived, not served: a delivery carries only the vendor's
                      own word, and the funnel is what the app makes of it. An
                      absent status is left blank rather than mapped, since the
                      fallback would read as a confident 'Pending'. */}
                  {e.status ? (
                    <span
                      style={{
                        display: 'inline-block',
                        opacity: latest ? 1 : 0.55,
                      }}
                    >
                      <StatusPill
                        funnel={funnelStatusFor({ vendor_status: e.status })}
                      />
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SuperuserAudit = () => {
  const [transactions, setTransactions] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [schools, setSchools] = useState([]);
  const [billingOverview, setBillingOverview] = useState(null);
  const [payOverview, setPayOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Closed to start: a month here is hundreds of rows, and the page is for
  // going and looking at one, not for reading all of them.
  const [expanded, setExpanded] = useState({});
  // Keyed by month, like `expanded`: the search belongs to the section it is
  // in, so opening August and filtering it leaves July's rows alone.
  const [queries, setQueries] = useState({});
  // Per-transaction webhook history, fetched the first time a row is opened.
  // Roughly a thousand deliveries sit behind these rows and almost none are
  // looked at, so they are not worth carrying in the list payload.
  const [openRows, setOpenRows] = useState({});
  const [events, setEvents] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const [txnRes, merchantRes, schoolRes, billRes, payRes] =
          await Promise.all([
            // Unconsolidated: the feed the other pages get has already cancelled
            // Wildfire's +/- reversal pairs and suppressed the negatives, which is
            // exactly what an audit needs to see.
            apiService.getAllTransactionsSuperuser({ consolidated: 'false' }),
            apiService.getBillableMerchants(),
            apiService.getSchools(),
            // Both status feeds are annotations on the figures; if either is down
            // the transactions still have to render.
            apiService.getBillingOverview().catch(() => null),
            apiService.getSchoolPayOverview().catch(() => null),
          ]);
        setTransactions(txnRes?.transactions || []);
        setMerchants(
          Array.isArray(merchantRes?.results) ? merchantRes.results : [],
        );
        setSchools(
          Array.isArray(schoolRes) ? schoolRes : schoolRes?.results || [],
        );
        setBillingOverview(billRes);
        setPayOverview(payRes);
      } catch (err) {
        console.error('Failed to load audit data:', err);
        setError('Failed to load transactions.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const billingState = useMemo(
    () => buildBillingStateIndex(billingOverview),
    [billingOverview],
  );
  const sentIndex = useMemo(() => buildSentIndex(payOverview), [payOverview]);

  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleRow = async (externalId) => {
    const nowOpen = !openRows[externalId];
    setOpenRows((prev) => ({ ...prev, [externalId]: nowOpen }));
    if (!nowOpen || events[externalId]) return;
    setEvents((prev) => ({ ...prev, [externalId]: { loading: true } }));
    try {
      const res = await apiService.getTransactionEvents(externalId);
      setEvents((prev) => ({
        ...prev,
        [externalId]: { rows: res?.events || [] },
      }));
    } catch (err) {
      console.error('Failed to load transaction events:', err);
      setEvents((prev) => ({ ...prev, [externalId]: { error: true } }));
    }
  };

  const schoolsById = useMemo(() => {
    const map = {};
    for (const s of schools) map[String(s.id)] = s;
    return map;
  }, [schools]);

  const grouped = useMemo(
    () => groupAuditByMonth(transactions, merchants, schoolsById, sentIndex),
    [transactions, merchants, schoolsById, sentIndex],
  );

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Dashboard" to="/superuser/dashboard/" />
            <div
              style={{ textAlign: 'center', padding: '3rem', color: '#666' }}
            >
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <style>{`
            .audit-txn-table { width: 100%; border-collapse: collapse; font-size: 13px; }
            /* Matches the grey column header on Merchant Invoices and School
               Pay: same background, size, weight and colour. */
            .audit-txn-table th {
              text-align: left; padding: 12px; font-size: 11px; font-weight: 700;
              text-transform: uppercase; letter-spacing: 0.05em; color: #999;
              white-space: nowrap; border-bottom: 1px solid #e9ecef;
            }
            .audit-txn-table th:first-child { padding-left: 20px; }
            .audit-txn-table td:first-child { padding-left: 20px; }
            .audit-txn-table th:last-child { padding-right: 20px; }
            .audit-txn-table td:last-child { padding-right: 20px; }
            .audit-txn-table td {
              padding: 14px 12px; border-top: 1px solid #f0f0f0; white-space: nowrap;
            }
            .audit-num { text-align: right; }
            /* The row is the control now, so it has to look like one. */
            .audit-txn-row { cursor: pointer; }
            .audit-txn-row:hover td { background: #f8fafb; }
            .audit-txn-row:focus-visible td:first-child { outline: 2px solid #00c8ff; outline-offset: -2px; }
          `}</style>
          <BackButton text="Back to Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Audit</h1>
          <p className="staff-page-subheader">
            Every transaction and where it sits in the funnel
          </p>

          {error && (
            <div style={{ marginTop: '24px', color: '#b00020' }}>{error}</div>
          )}

          {!error && grouped.length === 0 && (
            <div style={{ marginTop: '32px', color: '#666' }}>
              No transactions found.
            </div>
          )}

          {grouped.map((month) => {
            const open = Boolean(expanded[month.key]);
            const monthQuery = queries[month.key] || '';
            const rows = filterAuditTxns(month.txns, monthQuery);
            return (
              <div key={month.key} style={{ marginTop: '48px' }}>
                {/* Heading and subheading sit outside the card, exactly as they
                    do on Merchant Invoices and School Pay, so the three pages
                    scan as one section rather than three treatments. */}
                <h2
                  className="staff-section-header"
                  style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    marginBottom: '8px',
                  }}
                >
                  {month.label}
                </h2>
                <p
                  className="staff-page-content"
                  style={{ marginBottom: '16px' }}
                >
                  {month.txnCount} transaction{month.txnCount === 1 ? '' : 's'}
                </p>

                <div
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: 'white',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggle(month.key)}
                    aria-expanded={open}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '14px 20px',
                      border: 'none',
                      background: '#f8f9fa',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#666',
                    }}
                  >
                    <Chevron open={open} />
                    {open ? 'Hide transactions' : 'Show transactions'}
                  </button>

                  {open && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: '12px',
                          padding: '10px 20px',
                          borderTop: '1px solid #f0f0f0',
                        }}
                      >
                        {monthQuery.trim() && (
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#666',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {rows.length} of {month.txnCount}
                          </span>
                        )}
                        {/* Overrides .form-input's width:100% and 12px padding:
                            this is a filter tucked into a section header, not a
                            form field being filled in. */}
                        <input
                          type="search"
                          className="form-input"
                          value={monthQuery}
                          onChange={(e) =>
                            setQueries((prev) => ({
                              ...prev,
                              [month.key]: e.target.value,
                            }))
                          }
                          placeholder="Search…"
                          aria-label={`Search ${month.label} transactions`}
                          style={{
                            width: '220px',
                            flex: '0 0 auto',
                            padding: '7px 12px',
                            fontSize: '13px',
                          }}
                        />
                      </div>
                      {rows.length === 0 ? (
                        <div
                          style={{
                            padding: '18px 20px',
                            borderTop: '1px solid #f0f0f0',
                            color: '#666',
                            fontSize: '13px',
                          }}
                        >
                          No transactions in {month.label} match “
                          {monthQuery.trim()}”.
                        </div>
                      ) : (
                        <div
                          style={{
                            overflowX: 'auto',
                            borderTop: '1px solid #f0f0f0',
                          }}
                        >
                          <table className="audit-txn-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Source</th>
                                <th>Merchant</th>
                                <th>School</th>
                                <th>Status</th>
                                <th className="audit-num">Payment</th>
                                <th className="audit-num">School share</th>
                                <th>Received</th>
                                <th>Paid out</th>
                                <th>Fan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((t) => {
                                // Received is the vendor's own answer, folded from
                                // every delivery. Paid out comes from the statement,
                                // resolved in groupAuditByMonth against the month
                                // School Pay files it under — which is when the
                                // money arrived, not when the purchase happened.
                                const received =
                                  t.funnel === 'received' ||
                                  t.funnel === 'distributed';
                                // Only merchants we added to Olive are invoiced by
                                // ACH, so only they have a collection state worth
                                // showing. For every other source the vendor pays
                                // us directly and "received" is the whole story.
                                const invoiceState = t.billableMerchantId
                                  ? billingState.lookup(
                                      t.billableMerchantId,
                                      month.key,
                                    )
                                  : null;
                                const rowOpen = Boolean(openRows[t.externalId]);
                                const history = events[t.externalId];
                                return (
                                  <Fragment key={t.id}>
                                    <tr
                                      className="audit-txn-row"
                                      onClick={() => toggleRow(t.externalId)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          toggleRow(t.externalId);
                                        }
                                      }}
                                      tabIndex={0}
                                      role="button"
                                      aria-expanded={rowOpen}
                                      aria-label={
                                        rowOpen ? 'Hide updates' : 'Show updates'
                                      }
                                      style={{ opacity: t.nonBillable ? 0.55 : 1 }}
                                    >
                                      <td style={{ color: '#444' }}>
                                        {formatDate(t.date)}
                                      </td>
                                      <td style={{ color: '#666' }}>{t.source}</td>
                                      <td
                                        style={{
                                          maxWidth: '200px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {t.merchant}
                                      </td>
                                      <td
                                        style={{
                                          color: t.schoolName ? '#444' : '#999',
                                          maxWidth: '180px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {t.schoolName || '—'}
                                      </td>
                                      <td>
                                        <StatusPill funnel={t.funnel} />
                                      </td>
                                      <td
                                        className="audit-num"
                                        style={{
                                          color:
                                            t.cashback == null ? '#bbb' : '#666',
                                        }}
                                      >
                                        {t.cashback == null
                                          ? '—'
                                          : formatMoney(t.cashback)}
                                      </td>
                                      <td
                                        className="audit-num"
                                        style={{
                                          color: t.share ? '#1a1a1a' : '#bbb',
                                        }}
                                      >
                                        {t.share ? formatMoney(t.share) : '—'}
                                      </td>
                                      <td>
                                        {t.billableMerchantId ? (
                                          invoiceState && invoiceState.icon ? (
                                            <BillingStatusIcon
                                              state={invoiceState}
                                              size={16}
                                              showTitle={false}
                                            />
                                          ) : (
                                            <Awaiting
                                              label={
                                                invoiceState
                                                  ? invoiceState.label
                                                  : 'Not set up for ACH billing'
                                              }
                                            />
                                          )
                                        ) : received ? (
                                          <SentCheck
                                            sent
                                            label={`Received from ${t.source}`}
                                          />
                                        ) : t.funnel === 'pending' ? (
                                          <Awaiting label="Still with the vendor" />
                                        ) : (
                                          <Awaiting label="Nothing to receive" />
                                        )}
                                      </td>
                                      <td>
                                        {!t.schoolId || !t.share ? (
                                          <Awaiting label="Nothing owed to a school" />
                                        ) : t.statementSent ? (
                                          <SentCheck
                                            sent
                                            label={`Statement sent for ${t.payoutKey}`}
                                          />
                                        ) : (
                                          <Awaiting label="Statement not sent" />
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          color: t.fan ? '#444' : '#b00020',
                                          maxWidth: '220px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {t.fan || 'unattributed'}
                                      </td>
                                    </tr>

                                    {rowOpen && (
                                      <tr>
                                        <td
                                          colSpan={10}
                                          style={{
                                            background: '#fbfcfd',
                                            padding: 0,
                                          }}
                                        >
                                          {/* The cell spans all eleven columns, so
                                              on a table wide enough to scroll the
                                              panel is anchored to the far left and
                                              slides out of view the moment the user
                                              scrolls across — the row stays open
                                              but its updates are somewhere off
                                              screen. Sticking it to the left edge
                                              of the scroll container keeps it with
                                              the reader. Shrink-to-fit is what
                                              leaves it room to slide: at full cell
                                              width there is nothing to stick. */}
                                          <div
                                            style={{
                                              position: 'sticky',
                                              left: 0,
                                              width: 'fit-content',
                                            }}
                                          >
                                            <Updates history={history} />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SuperuserAudit;
